/**
 * How the renderer reads a snapshot: its reference to it, its sections loaded
 * once each, and the client names of its `namen` section. Nothing here reaches
 * the dashboard's API client: `sections.ts` wires it to the API.
 */

import type { AppSnapshotInfo } from "@/lib/api";

import type { BezettingSection, BezettingSectionLoader, BezettingSnapshotRef } from "./contract";

/** The renderer's reference to a snapshot, from the API's summary of it. */
export function snapshotRef(info: AppSnapshotInfo): BezettingSnapshotRef {
  return {
    id: info.id,
    asOf: info.as_of,
    odooReadAt: info.odoo_read_at,
    computedAt: info.computed_at,
    sections: info.sections.map((section) => section.key),
  };
}

export function isSection(value: unknown): value is BezettingSection {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    typeof (value as { section?: unknown }).section === "string"
  );
}

export interface CachedSectionsOptions {
  /** The request for one section of the snapshot. */
  fetch: (key: string) => Promise<unknown>;
  /** The error code a failure carries ("section_not_found"), else null. */
  codeOf: (error: unknown) => string | null;
  /** A load that failed, just before it rejects. */
  onFailure?: (error: unknown) => void;
}

/**
 * The sections of one snapshot. `load` fetches a section at most once per
 * loader (a snapshot never changes); a failed load is forgotten, so asking
 * again tries again. `tryLoad` answers null, without a request, for a key the
 * snapshot does not list, and for one the server says it does not hold.
 */
export function cachedSectionLoader(
  snapshot: BezettingSnapshotRef,
  { fetch, codeOf, onFailure }: CachedSectionsOptions,
): BezettingSectionLoader {
  const listed = new Set(snapshot.sections);
  const loads = new Map<string, Promise<BezettingSection>>();

  const load = (key: string): Promise<BezettingSection> => {
    const known = loads.get(key);
    if (known) {
      return known;
    }
    const pending = fetch(key)
      .then((value) => {
        if (!isSection(value)) {
          throw new Error(`Section ${key} is not a section`);
        }
        return value;
      })
      .catch((error: unknown) => {
        loads.delete(key);
        onFailure?.(error);
        throw error;
      });
    loads.set(key, pending);
    return pending;
  };

  return {
    load,
    tryLoad: async (key) => {
      if (!listed.has(key)) {
        return null;
      }
      try {
        return await load(key);
      } catch (error) {
        if (codeOf(error) === "section_not_found") {
          return null;
        }
        throw error;
      }
    },
  };
}

/** A compact table of a section: `{cols, rows}`. */
interface Table {
  cols: unknown[];
  rows: unknown[][];
}

function isTable(value: unknown): value is Table {
  const table = value as Partial<Table> | null;
  return Array.isArray(table?.cols) && Array.isArray(table?.rows);
}

/**
 * Client id to name, from the `namen` section's `clients` table
 * (`client_id`, `name`); empty without the section or with another shape.
 */
export function namesOf(section: BezettingSection | null): ReadonlyMap<number, string> {
  const table = section?.clients;
  if (!isTable(table)) {
    return new Map();
  }
  const idAt = table.cols.indexOf("client_id");
  const nameAt = table.cols.indexOf("name");
  if (idAt < 0 || nameAt < 0) {
    return new Map();
  }
  const names = new Map<number, string>();
  for (const row of table.rows) {
    const id = Number(row[idAt]);
    const name = row[nameAt];
    if (Number.isInteger(id) && typeof name === "string") {
      names.set(id, name);
    }
  }
  return names;
}
